import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Newspaper, Shield, TrendingUp, Cpu, Building2, Scale, 
  Brain, ExternalLink, RefreshCw, Loader2, AlertTriangle,
  Calendar, Search, Filter, Globe, Zap, DollarSign
} from 'lucide-react';

// Serviço gratuito para converter RSS para JSON (sem necessidade de backend)
const RSS2JSON_API = 'https://api.rss2json.com/v1/api.json?rss_url=';

const NewsPage = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [news, setNews] = useState({
    security: [],
    fintech: [],
    ai: [],
    regulatory: [],
    technology: []
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [visibleCount, setVisibleCount] = useState(30);

  const categories = [
    { id: 'all', label: 'Todas', icon: Globe, color: 'from-gray-500 to-gray-600' },
    { id: 'security', label: 'Cybersegurança', icon: Shield, color: 'from-red-500 to-orange-500' },
    { id: 'ai', label: 'Inteligência Artificial', icon: Brain, color: 'from-purple-500 to-pink-500' },
    { id: 'fintech', label: 'FinTech & Mercado', icon: DollarSign, color: 'from-green-500 to-emerald-500' },
    { id: 'regulatory', label: 'Regulatório & GRC', icon: Scale, color: 'from-blue-500 to-cyan-500' },
    { id: 'technology', label: 'Tecnologia', icon: Cpu, color: 'from-amber-500 to-yellow-500' },
  ];

  // RSS Feeds por categoria
  const feedsByCategory = {
    security: [
      { url: 'https://feeds.feedburner.com/TheHackersNews', source: 'The Hacker News' },
      { url: 'https://www.bleepingcomputer.com/feed/', source: 'Bleeping Computer' },
      { url: 'https://krebsonsecurity.com/feed/', source: 'Krebs on Security' },
      { url: 'https://www.darkreading.com/rss.xml', source: 'Dark Reading' },
    ],
    fintech: [
      { url: 'https://www.finextra.com/rss/headlines.aspx', source: 'Finextra' },
      { url: 'https://cointelegraph.com/rss', source: 'Cointelegraph' },
      { url: 'https://www.pymnts.com/feed/', source: 'PYMNTS' },
    ],
    ai: [
      // Feeds Internacionais
      { url: 'https://www.marktechpost.com/feed/', source: 'MarkTechPost' },
      { url: 'https://openai.com/news/rss.xml', source: 'OpenAI' },
      { url: 'https://www.artificialintelligence-news.com/feed/', source: 'AI News' },
      { url: 'https://venturebeat.com/category/ai/feed/', source: 'VentureBeat AI' },
      { url: 'https://www.unite.ai/feed/', source: 'Unite.AI' },
      { url: 'https://syncedreview.com/feed/', source: 'Synced Review' },
      // Feeds Brasileiros - Tecnologia e IA
      { url: 'https://canaltech.com.br/rss/inteligencia-artificial/', source: 'Canaltech Brasil' },
      { url: 'https://olhardigital.com.br/feed/', source: 'Olhar Digital' },
      { url: 'https://tecnoblog.net/feed/', source: 'Tecnoblog' },
      { url: 'https://www.tecmundo.com.br/rss', source: 'TecMundo' },
    ],
    regulatory: [
      { url: 'https://iapp.org/news/rss/', source: 'IAPP Privacy' },
      { url: 'https://www.jdsupra.com/law-news/rss/topic/data-privacy/', source: 'JD Supra Privacy' },
      { url: 'https://www.jdsupra.com/law-news/rss/topic/cybersecurity/', source: 'JD Supra Cyber' },
      { url: 'https://gdpr.eu/feed/', source: 'GDPR.eu' },
      { url: 'https://www.dataprotectionreport.com/feed/', source: 'Data Protection Report' },
    ],
    technology: [
      { url: 'https://techcrunch.com/feed/', source: 'TechCrunch' },
      { url: 'https://www.wired.com/feed/rss', source: 'Wired' },
      { url: 'https://arstechnica.com/feed/', source: 'Ars Technica' },
      { url: 'https://www.theverge.com/rss/index.xml', source: 'The Verge' },
      // Sites Brasileiros
      { url: 'https://canaltech.com.br/rss/', source: 'Canaltech' },
      { url: 'https://gizmodo.uol.com.br/feed/', source: 'Gizmodo Brasil' },
    ]
  };

  useEffect(() => {
    fetchAllNews();
  }, []);

  const fetchRssFeed = async (feedUrl, source, category) => {
    try {
      const response = await fetch(`${RSS2JSON_API}${encodeURIComponent(feedUrl)}`);
      const data = await response.json();
      
      if (data.status === 'ok' && data.items) {
        return data.items.slice(0, 5).map(item => ({
          title: item.title || '',
          description: item.description ? item.description.replace(/<[^>]*>/g, '').substring(0, 200) : '',
          link: item.link || '',
          pub_date: item.pubDate || '',
          source: source,
          category: category
        }));
      }
      return [];
    } catch (error) {
      console.warn(`Erro ao buscar ${source}:`, error);
      return [];
    }
  };

  const fetchAllNews = async () => {
    setLoading(true);
    
    const newNews = {
      security: [],
      fintech: [],
      ai: [],
      regulatory: [],
      technology: []
    };

    // Buscar todas as categorias em paralelo
    const fetchPromises = [];
    
    for (const [category, feeds] of Object.entries(feedsByCategory)) {
      for (const feed of feeds) {
        fetchPromises.push(
          fetchRssFeed(feed.url, feed.source, category).then(articles => ({
            category,
            articles
          }))
        );
      }
    }

    try {
      const results = await Promise.allSettled(fetchPromises);
      
      results.forEach(result => {
        if (result.status === 'fulfilled' && result.value) {
          const { category, articles } = result.value;
          newNews[category] = [...newNews[category], ...articles];
        }
      });

      setNews(newNews);
    } catch (error) {
      console.error('Erro ao buscar notícias:', error);
    } finally {
      setLoading(false);
    }
  };

  const getAllNews = () => {
    let allNews = [];
    Object.keys(news).forEach(category => {
      if (Array.isArray(news[category])) {
        allNews = [...allNews, ...news[category].map(n => ({ ...n, category }))];
      }
    });
    return allNews.sort((a, b) => new Date(b.pub_date || 0) - new Date(a.pub_date || 0));
  };

  const getFilteredNews = () => {
    let filtered = activeCategory === 'all' 
      ? getAllNews() 
      : (news[activeCategory] || []).map(n => ({ ...n, category: activeCategory }));
    
    if (searchTerm) {
      filtered = filtered.filter(article => 
        article.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        article.description?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    
    return filtered;
  };

  const getCategoryInfo = (categoryId) => {
    return categories.find(c => c.id === categoryId) || categories[0];
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    try {
      const date = new Date(dateStr);
      return date.toLocaleDateString('pt-BR', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const filteredNews = getFilteredNews();

  return (
    <>
      <Helmet>
        <title>Notícias & Intelligence - DMM Security | Cybersegurança, IA e Mercado</title>
        <meta name="description" content="Central de notícias e inteligência: cybersegurança, IA, fintech, GRC, regulatório e tendências tecnológicas. Atualização em tempo real." />
      </Helmet>

      <div className="min-h-screen pt-20 bg-gray-50" data-testid="news-page">
        {/* Hero Section */}
        <section className="py-16 bg-gradient-to-br from-[#0f2535] via-[#1a3a52] to-[#1e4d6b] text-white relative overflow-hidden">
          <div className="absolute inset-0 opacity-10">
            <div className="absolute inset-0" style={{
              backgroundImage: `radial-gradient(circle at 20% 50%, rgba(16,185,129,0.3) 0%, transparent 50%), radial-gradient(circle at 80% 50%, rgba(59,130,246,0.3) 0%, transparent 50%)`
            }} />
          </div>
          
          <div className="container mx-auto px-4 relative">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="max-w-4xl mx-auto text-center"
            >
              <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#10b981]/20 text-[#10b981] text-sm font-semibold rounded-full mb-6">
                <Zap className="w-4 h-4" />
                Atualização em Tempo Real
              </span>
              <h1 className="text-4xl md:text-5xl font-bold mb-4">
                Central de Notícias & Intelligence
              </h1>
              <p className="text-xl text-gray-300 leading-relaxed">
                Cybersegurança, Inteligência Artificial, FinTech, GRC e tendências tecnológicas em um só lugar
              </p>
            </motion.div>
          </div>
        </section>

        {/* Main Content */}
        <section className="py-12">
          <div className="container mx-auto px-4">
            {/* Search and Filter Bar */}
            <div className="bg-white rounded-2xl shadow-sm p-6 mb-8">
              <div className="flex flex-col lg:flex-row gap-4 items-center justify-between">
                {/* Search */}
                <div className="relative w-full lg:w-96">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Buscar notícias..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 focus:ring-2 focus:ring-[#10b981] focus:border-transparent transition-all"
                    data-testid="news-search"
                  />
                </div>

                {/* Refresh Button */}
                <button
                  onClick={fetchAllNews}
                  disabled={loading}
                  className="flex items-center gap-2 px-5 py-3 bg-[#10b981] text-white rounded-xl hover:bg-[#0ea573] transition-colors font-medium"
                  data-testid="refresh-news-btn"
                >
                  <RefreshCw className={`w-5 h-5 ${loading ? 'animate-spin' : ''}`} />
                  Atualizar
                </button>
              </div>

              {/* Categories */}
              <div className="flex flex-wrap gap-2 mt-6">
                {categories.map((cat) => (
                  <button
                    key={cat.id}
                    onClick={() => {
                      setActiveCategory(cat.id);
                      setVisibleCount(30);
                    }}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium text-sm transition-all duration-300 ${
                      activeCategory === cat.id
                        ? 'bg-[#1a3a52] text-white shadow-lg'
                        : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                    }`}
                    data-testid={`category-${cat.id}`}
                  >
                    <cat.icon className="w-4 h-4" />
                    {cat.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Stats Bar */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-8">
              {categories.slice(1).map((cat) => (
                <div key={cat.id} className="bg-white rounded-xl p-4 shadow-sm">
                  <div className="flex items-center gap-3">
                    <div className={`w-10 h-10 rounded-lg bg-gradient-to-br ${cat.color} flex items-center justify-center`}>
                      <cat.icon className="w-5 h-5 text-white" />
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-[#1a3a52]">
                        {Array.isArray(news[cat.id]) ? news[cat.id].length : 0}
                      </p>
                      <p className="text-xs text-gray-500">{cat.label}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* News Grid */}
            {loading ? (
              <div className="flex flex-col items-center justify-center py-20">
                <Loader2 className="w-12 h-12 animate-spin text-[#10b981] mb-4" />
                <p className="text-gray-600">Carregando notícias de múltiplas fontes...</p>
              </div>
            ) : filteredNews.length === 0 ? (
              <div className="text-center py-20">
                <Newspaper className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                <p className="text-gray-600 text-lg">Nenhuma notícia encontrada</p>
                <p className="text-gray-400">Tente ajustar os filtros ou buscar outros termos</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredNews.slice(0, visibleCount).map((article, index) => {
                  const catInfo = getCategoryInfo(article.category);
                  return (
                    <motion.a
                      key={index}
                      href={article.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ duration: 0.3, delay: Math.min(index, 30) * 0.03 }}
                      className="group bg-white rounded-2xl overflow-hidden shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 hover:border-[#10b981]/30"
                      data-testid={`news-card-${index}`}
                    >
                      {/* Category Badge */}
                      <div className="p-5">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <div className={`w-8 h-8 rounded-lg bg-gradient-to-br ${catInfo.color} flex items-center justify-center`}>
                              <catInfo.icon className="w-4 h-4 text-white" />
                            </div>
                            <span className="text-xs font-semibold text-gray-500 uppercase">
                              {catInfo.label}
                            </span>
                          </div>
                          <ExternalLink className="w-4 h-4 text-gray-300 group-hover:text-[#10b981] transition-colors" />
                        </div>

                        {/* Title */}
                        <h3 className="text-lg font-bold text-[#1a3a52] mb-2 group-hover:text-[#10b981] transition-colors line-clamp-2">
                          {article.title}
                        </h3>

                        {/* Description */}
                        {article.description && (
                          <p className="text-sm text-gray-600 mb-4 line-clamp-3">
                            {article.description}
                          </p>
                        )}

                        {/* Footer */}
                        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
                          <span className="text-xs text-gray-400 flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {formatDate(article.pub_date)}
                          </span>
                          <span className="text-xs font-medium text-[#10b981] bg-[#10b981]/10 px-2 py-1 rounded">
                            {article.source}
                          </span>
                        </div>
                      </div>
                    </motion.a>
                  );
                })}
              </div>
            )}

            {/* Load More */}
            {filteredNews.length > visibleCount && (
              <div className="text-center mt-8 space-y-4">
                <p className="text-gray-500">
                  Mostrando {Math.min(visibleCount, filteredNews.length)} de {filteredNews.length} notícias
                </p>
                <button
                  onClick={() => setVisibleCount(prev => prev + 30)}
                  className="inline-flex items-center gap-2 px-6 py-3 bg-[#10b981] text-white rounded-xl hover:bg-[#0ea573] transition-colors font-medium shadow-lg hover:shadow-xl"
                  data-testid="load-more-btn"
                >
                  <Newspaper className="w-5 h-5" />
                  Carregar Mais Notícias
                </button>
              </div>
            )}

            {/* All loaded message */}
            {filteredNews.length > 30 && visibleCount >= filteredNews.length && (
              <div className="text-center mt-8">
                <p className="text-gray-500 flex items-center justify-center gap-2">
                  <Globe className="w-4 h-4" />
                  Todas as {filteredNews.length} notícias carregadas
                </p>
              </div>
            )}
          </div>
        </section>

        {/* AI Spotlight Section */}
        <section className="py-16 bg-gradient-to-br from-purple-900 via-[#1a3a52] to-[#0f2535] text-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-purple-500/20 text-purple-300 text-sm font-semibold rounded-full mb-4">
                <Brain className="w-4 h-4" />
                Destaque IA
              </span>
              <h2 className="text-3xl md:text-4xl font-bold mb-4">
                Inteligência Artificial no Mercado
              </h2>
              <p className="text-lg text-gray-300 max-w-2xl mx-auto">
                As últimas novidades de IA aplicada ao mercado financeiro, cybersegurança e tecnologia
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-6xl mx-auto">
              {(news.ai || []).slice(0, 6).map((article, index) => (
                <motion.a
                  key={index}
                  href={article.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.1 }}
                  className="group bg-white/5 backdrop-blur-sm rounded-2xl p-6 border border-white/10 hover:border-purple-500/50 transition-all hover:bg-white/10"
                >
                  <Brain className="w-10 h-10 text-purple-400 mb-4" />
                  <h3 className="text-lg font-bold text-white mb-2 group-hover:text-purple-300 transition-colors line-clamp-2">
                    {article.title}
                  </h3>
                  <p className="text-sm text-gray-400 line-clamp-2 mb-4">
                    {article.description}
                  </p>
                  <div className="flex items-center justify-between">
                    <span className="text-xs text-gray-500">{article.source}</span>
                    <ExternalLink className="w-4 h-4 text-purple-400" />
                  </div>
                </motion.a>
              ))}
            </div>
          </div>
        </section>

        {/* AI Tools & Trends Section */}
        <section className="py-16 bg-gradient-to-br from-gray-50 to-white">
          <div className="container mx-auto px-4">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <span className="inline-flex items-center gap-2 px-4 py-1.5 bg-[#10b981]/10 text-[#10b981] text-sm font-semibold rounded-full mb-4">
                <Zap className="w-4 h-4" />
                Ferramentas em Alta
              </span>
              <h2 className="text-3xl md:text-4xl font-bold text-[#1a3a52] mb-4">
                IAs Gratuitas que Você Precisa Conhecer
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                As melhores ferramentas de inteligência artificial para produtividade, criação de conteúdo e muito mais
              </p>
            </motion.div>

            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4 max-w-5xl mx-auto">
              {[
                { name: 'ChatGPT', desc: 'Assistente de texto', url: 'https://chat.openai.com', color: 'from-green-400 to-emerald-500' },
                { name: 'DeepSeek', desc: 'Raciocínio lógico', url: 'https://deepseek.com', color: 'from-blue-400 to-indigo-500' },
                { name: 'Google Gemini', desc: 'IA do Google', url: 'https://gemini.google.com', color: 'from-blue-500 to-purple-500' },
                { name: 'Claude', desc: 'IA da Anthropic', url: 'https://claude.ai', color: 'from-orange-400 to-red-500' },
                { name: 'Perplexity', desc: 'Busca com IA', url: 'https://perplexity.ai', color: 'from-cyan-400 to-blue-500' },
                { name: 'Manus IA', desc: 'IA Brasileira', url: 'https://www.manus.ai', color: 'from-green-500 to-yellow-500' },
                { name: 'CapCut', desc: 'Editor de vídeo', url: 'https://capcut.com', color: 'from-pink-400 to-rose-500' },
                { name: 'Canva AI', desc: 'Design com IA', url: 'https://canva.com', color: 'from-purple-400 to-pink-500' },
                { name: 'ElevenLabs', desc: 'Geração de voz', url: 'https://elevenlabs.io', color: 'from-indigo-400 to-purple-500' },
                { name: 'GitHub Copilot', desc: 'Código com IA', url: 'https://github.com/features/copilot', color: 'from-gray-600 to-gray-800' },
              ].map((tool, index) => (
                <motion.a
                  key={index}
                  href={tool.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, scale: 0.9 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  whileHover={{ scale: 1.05, y: -5 }}
                  className="group relative bg-white rounded-2xl p-4 shadow-sm hover:shadow-xl transition-all duration-300 border border-gray-100 text-center"
                >
                  <div className={`w-12 h-12 mx-auto mb-3 rounded-xl bg-gradient-to-br ${tool.color} flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="font-bold text-[#1a3a52] text-sm group-hover:text-[#10b981] transition-colors">
                    {tool.name}
                  </h4>
                  <p className="text-xs text-gray-500 mt-1">{tool.desc}</p>
                </motion.a>
              ))}
            </div>

            {/* AI Trends Cards */}
            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-6 max-w-5xl mx-auto">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                className="bg-gradient-to-br from-purple-600 to-indigo-700 rounded-2xl p-6 text-white"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <TrendingUp className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-purple-200">Tendência 2025</span>
                </div>
                <h3 className="text-xl font-bold mb-2">Agentes de IA Autônomos</h3>
                <p className="text-sm text-purple-100">IAs que executam tarefas complexas de forma independente, como o Codex da OpenAI e Devin da Cognition</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-gradient-to-br from-emerald-600 to-teal-700 rounded-2xl p-6 text-white"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Globe className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-emerald-200">Destaque Brasil</span>
                </div>
                <h3 className="text-xl font-bold mb-2">IA em Português Avançando</h3>
                <p className="text-sm text-emerald-100">Brasil e Portugal firmaram acordo para criar LLM 100% em português. Ferramentas como Manus IA ganham força</p>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-gradient-to-br from-orange-600 to-red-700 rounded-2xl p-6 text-white"
              >
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-white/20 rounded-lg flex items-center justify-center">
                    <Shield className="w-5 h-5" />
                  </div>
                  <span className="text-sm font-medium text-orange-200">Alerta</span>
                </div>
                <h3 className="text-xl font-bold mb-2">IA e Segurança: Atenção!</h3>
                <p className="text-sm text-orange-100">Aumento de 308% em deepfakes e conteúdo falso gerado por IA no Brasil. Saiba como se proteger</p>
              </motion.div>
            </div>
          </div>
        </section>

        {/* Info Footer */}
        <section className="py-8 bg-white">
          <div className="container mx-auto px-4 text-center">
            <p className="text-gray-500 text-sm flex items-center justify-center gap-2">
              <RefreshCw className="w-4 h-4" />
              Notícias atualizadas automaticamente de múltiplas fontes globais
            </p>
          </div>
        </section>
      </div>
    </>
  );
};

export default NewsPage;
